import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, ShieldCheck, ShieldAlert, FileX2, Loader2, Copy, Tag, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatWIBPanjang } from '@/lib/datetime';
import { ambilInfoTemplate, formatUkuran } from '@/lib/pkgTemplate';
import IndikatorChecksumTemplate, { ringkasKecocokan } from '@/components/pkg/IndikatorChecksumTemplate';

const STATUS = {
  cocok: { label: 'Checksum cocok', kelas: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck },
  berbeda: { label: 'Checksum berbeda', kelas: 'bg-rose-100 text-rose-700 border-rose-200', icon: ShieldAlert },
  hilang: { label: 'Berkas tidak ditemukan', kelas: 'bg-rose-100 text-rose-700 border-rose-200', icon: FileX2 },
};

const ringkasChecksum = (c) => (c ? `${c.slice(0, 12)}…${c.slice(-8)}` : '-');

function salin(teks) {
  navigator.clipboard?.writeText(teks).then(() => toast.success('Checksum disalin'), () => toast.error('Gagal menyalin'));
}

// Info versi template resmi PKG: berkas instrumen & rekap yang dipakai cetak, versinya, dan verifikasi checksum
// (GET /pkg/template/info). Kepala madrasah dapat meminta pemeriksaan ulang berkas di server.
export default function PkgTemplatePage() {
  const { keanggotaan } = useOutletContext() || {};
  const [info, setInfo] = useState(null);
  const [memeriksa, setMemeriksa] = useState(false);

  const muat = useCallback(async (periksaUlang = false) => {
    setMemeriksa(periksaUlang);
    try {
      setInfo(await ambilInfoTemplate({ periksaUlang }));
      if (periksaUlang) toast.success('Berkas template diperiksa ulang');
    } catch {
      toast.error('Gagal memuat info template');
    } finally {
      setMemeriksa(false);
    }
  }, []);

  useEffect(() => { muat(); }, [muat]);

  if (!info) return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#006837]" /></div>;

  const { cocok: semuaCocok } = ringkasKecocokan(info.berkas);

  return (
    <div className="space-y-4" data-testid="pkg-template">
      <div className={`flex gap-2 rounded-lg border p-3 text-sm ${semuaCocok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`} data-testid="pkg-template-status">
        {semuaCocok ? <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" /> : <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />}
        <span>
          {semuaCocok
            ? `Semua berkas template resmi versi ${info.versi_template} tersedia dan utuh.`
            : 'Ada berkas template yang hilang atau berubah. Cetak dokumen resmi dapat tidak sesuai; hubungi admin sistem.'}
          <span className="block text-xs opacity-80">Diperiksa {formatWIBPanjang(info.diperiksa_pada)}</span>
        </span>
        {keanggotaan?.peran === 'kepala' && (
          <Button size="sm" variant="ghost" className="ml-auto h-8 shrink-0 gap-1" onClick={() => muat(true)} disabled={memeriksa} data-testid="pkg-template-periksa-ulang">
            {memeriksa ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Periksa ulang
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {info.berkas.map((b) => {
          const st = STATUS[b.status] || STATUS.berbeda;
          return (
            <Card key={b.jenis} data-testid={`pkg-template-${b.jenis}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-[#006837]" /> {b.judul}</CardTitle>
                  <Badge className={`${st.kelas} shrink-0 gap-1`}><st.icon className="h-3 w-3" /> {st.label}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between gap-4"><span className="text-slate-500">Nama berkas</span><span className="font-mono text-xs text-slate-800 break-all text-right">{b.nama_berkas}</span></div>
                <div className="flex justify-between gap-4"><span className="text-slate-500">Versi</span><span className="inline-flex items-center gap-1 text-slate-800"><Tag className="h-3 w-3" /> {b.versi}</span></div>
                <div className="flex justify-between gap-4"><span className="text-slate-500">Ukuran</span><span className="tabular-nums text-slate-800">{formatUkuran(b.ukuran)}</span></div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-slate-500">SHA-256</span>
                  <span className="flex items-center gap-1">
                    <span className="font-mono text-xs text-slate-800" title={b.checksum || ''}>{ringkasChecksum(b.checksum)}</span>
                    {b.checksum && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => salin(b.checksum)} aria-label="Salin checksum"><Copy className="h-3 w-3" /></Button>
                    )}
                  </span>
                </div>
                <IndikatorChecksumTemplate berkas={b} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-slate-500">
        Berkas template hanya dibaca dari folder internal aplikasi dan tidak pernah ditimpa; hasil cetak ditulis ke salinan sementara.
      </p>
    </div>
  );
}
